// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Modal, Badge, DataTable, Button } from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';
import { formatDateTime } from '@loa/theme';
import { FundRun, FundRunStatus, ScraperResult, ScraperStatus } from '../api/fundsApi';
import './FundRunDetailModal.css';

export const RUN_STATUS_LABELS: Record<FundRunStatus, string> = {
  running: 'Running',
  completed: 'Completed',
  partial_failure: 'Partial failure',
  failed: 'Failed',
};

export function runStatusColor(status: FundRunStatus): 'blue' | 'green' | 'yellow' | 'red' {
  switch (status) {
    case 'running':
      return 'blue';
    case 'completed':
      return 'green';
    case 'partial_failure':
      return 'yellow';
    case 'failed':
    default:
      return 'red';
  }
}

export function scraperStatusColor(status: ScraperStatus): 'green' | 'red' | 'grey' {
  switch (status) {
    case 'success':
      return 'green';
    case 'failed':
      return 'red';
    case 'skipped':
    default:
      return 'grey';
  }
}

export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) {
    return '—';
  }
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest > 0 ? `${minutes}m ${rest}s` : `${minutes}m`;
}

interface FundRunDetailModalProps {
  run: FundRun | null;
  open: boolean;
  onClose: () => void;
}

const SCRAPER_COLUMNS: DataTableColumn<ScraperResult>[] = [
  {
    key: 'name',
    header: 'Provider',
    render: (s) => s.name,
  },
  {
    key: 'status',
    header: 'Status',
    width: '110px',
    render: (s) => <Badge color={scraperStatusColor(s.status)} size="sm">{s.status}</Badge>,
  },
  {
    key: 'funds',
    header: 'Funds',
    width: '90px',
    render: (s) => s.funds_count,
  },
  {
    key: 'duration',
    header: 'Duration',
    width: '110px',
    render: (s) => formatDuration(s.duration_seconds),
  },
  {
    key: 'error',
    header: 'Error',
    render: (s) =>
      s.error ? <span className="fund-run-detail__error-text">{s.error}</span> : '—',
  },
];

export default function FundRunDetailModal({ run, open, onClose }: FundRunDetailModalProps) {
  if (!run) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header title="Retrieval run detail" onClose={onClose} />
      <Modal.Content>
        <div className="fund-run-detail__summary">
          <div className="fund-run-detail__summary-item">
            <span className="fund-run-detail__label">Run ID</span>
            <span className="fund-run-detail__value fund-run-detail__value--mono">{run.run_id}</span>
          </div>
          <div className="fund-run-detail__summary-item">
            <span className="fund-run-detail__label">Status</span>
            <Badge color={runStatusColor(run.status)} size="sm">
              {RUN_STATUS_LABELS[run.status]}
            </Badge>
          </div>
          <div className="fund-run-detail__summary-item">
            <span className="fund-run-detail__label">Trigger</span>
            <span className="fund-run-detail__value">{run.trigger}</span>
          </div>
          <div className="fund-run-detail__summary-item">
            <span className="fund-run-detail__label">Started</span>
            <span className="fund-run-detail__value">{formatDateTime(run.started_at)}</span>
          </div>
          <div className="fund-run-detail__summary-item">
            <span className="fund-run-detail__label">Finished</span>
            <span className="fund-run-detail__value">
              {run.finished_at ? formatDateTime(run.finished_at) : 'In progress'}
            </span>
          </div>
          <div className="fund-run-detail__summary-item">
            <span className="fund-run-detail__label">Duration</span>
            <span className="fund-run-detail__value">{formatDuration(run.duration_seconds)}</span>
          </div>
          <div className="fund-run-detail__summary-item">
            <span className="fund-run-detail__label">Funds retrieved</span>
            <span className="fund-run-detail__value">{run.total_funds.toLocaleString()}</span>
          </div>
          <div className="fund-run-detail__summary-item">
            <span className="fund-run-detail__label">Scrapers</span>
            <span className="fund-run-detail__value">
              {run.scrapers_passed}/{run.total_scrapers} passed
            </span>
          </div>
        </div>

        {run.reconciled_on_boot && (
          <p className="fund-run-detail__note">
            This run was interrupted by an aggregator restart and marked failed on boot.
          </p>
        )}

        <h4 className="fund-run-detail__scrapers-heading">Per-provider results</h4>
        <DataTable
          columns={SCRAPER_COLUMNS}
          data={run.scrapers}
          rowKey={(s) => s.scraper_id}
          emptyState={<span>No per-provider results recorded for this run.</span>}
        />

        <div className="fund-run-detail__actions">
          <Button variant="outlined" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
}
